import { Button, Color, Link, Text } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import find from 'lodash/find'
import React, { ReactElement, useCallback, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import { EmberSaveAccountProps } from '@features/chartOfAccounts/types/emberSaveAccountProps'
import { useAccountGroupsWithSums } from '@modules-deprecated/app/accounts'
import { fetchAccount } from '@modules-deprecated/app/accounts/query-api'

import { QueryKeys } from '../../../../../../enums/queryKeys'
import { EmberEventFn } from '../../../../../../types/emberEventFn'
import { EditAccountModal } from '../../../../../settings/routes/OrganizationChartOfAccounts/elements/EditAccountModal'
import { getEditAccountAllowedFields } from '../../../../../settings/routes/OrganizationChartOfAccounts/elements/EditAccountModal/utils/getEditAccountAllowedFields'

type EditAccountActionProps = {
  accountId: string
  isLinkButton?: boolean
  text: string
  groupId: string
  onAccountSave: EmberEventFn
}

export const EditAccountAction = ({
  accountId,
  isLinkButton = false,
  text,
  groupId,
  onAccountSave,
}: EditAccountActionProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const { accountGroups = [] } = useAccountGroupsWithSums()
  const queryClient = useQueryClient()

  const {
    isLoading: isAccountFetchLoading,
    isError: isAccountFetchError,
    error: accountFetchError,
    data,
  } = useQuery([QueryKeys.Account, accountId], () => fetchAccount(accountId), {
    enabled: !!accountId,
  })

  if (isAccountFetchError) {
    trackError(accountFetchError)
  }

  const isEditDisabled = !accountId || isAccountFetchLoading || isAccountFetchError

  const getAccountGroup = useCallback(() => {
    return find(accountGroups, ({ id }) => groupId === id)
  }, [accountGroups, groupId])

  const toggleEditor = () => {
    setIsOpen(!isOpen)
  }

  const handleModalSaved = useCallback(
    (response?: EmberSaveAccountProps) => {
      onAccountSave({ detail: response })
      queryClient.invalidateQueries(QueryKeys.Account)
    },
    [onAccountSave, queryClient],
  )

  return (
    <>
      {isLinkButton ? (
        <Text weight="medium" color={Color.Purple800}>
          <Link disabled={isEditDisabled} onClick={toggleEditor}>
            {text}
          </Link>
        </Text>
      ) : (
        <Button onClick={toggleEditor} disabled={isEditDisabled} variant="outlined">
          {text}
        </Button>
      )}

      <EditAccountModal
        isOpen={isOpen}
        account={data?.account}
        group={groupId ? getAccountGroup() : undefined}
        onClose={toggleEditor}
        onSave={handleModalSaved}
        allowedFields={getEditAccountAllowedFields(!!data?.account?.predefinedAccount)}
      />
    </>
  )
}
